<script setup>
defineOptions({ name: "WhatsappModulePage" });
import { defineAsyncComponent } from "vue";
import {
  BellAlertIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
  DocumentCheckIcon,
  ShoppingCartIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  CalendarDaysIcon,
} from "@heroicons/vue/24/outline";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import heroBg from "@/assets/hero-wpp.webp";

const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));
const portalUrl =
  import.meta.env?.VITE_PORTAL_URL || "https://portal.kalisistemas.com.br";

useSeo({
  title: "O Kali Gestão trabalha. Seu cliente fica sabendo. | Kali Sistemas",
  description:
    "Automatize mensagens pelo WhatsApp a partir das ações realizadas no Kali Gestão: confirmações, atualizações de pedido, lembretes e documentos.",
  image: heroBg,
});

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// Capacidades reais do adicional WhatsApp, descritas no material do produto.
const capacidades = [
  {
    icon: BellAlertIcon,
    titulo: "Automatize mensagens",
    texto: "Configure comunicações para diferentes momentos da operação.",
  },
  {
    icon: ArrowPathIcon,
    titulo: "Reduza tarefas repetitivas",
    texto: "Evite que sua equipe precise enviar manualmente as mesmas mensagens todos os dias.",
  },
  {
    icon: ChatBubbleLeftRightIcon,
    titulo: "Padronize a comunicação",
    texto: "Utilize mensagens previamente definidas para manter um padrão de atendimento.",
  },
  {
    icon: DocumentCheckIcon,
    titulo: "Mantenha o cliente informado",
    texto: "Envie atualizações relacionadas ao andamento de pedidos, atendimentos e agendamentos.",
  },
];

const steps = [
  {
    title: "Algo acontece no Kali Gestão",
    description: "Um pedido é confirmado, um agendamento é realizado ou outra ação configurada acontece.",
  },
  {
    title: "O sistema identifica o evento",
    description: "O Kali Gestão reconhece que existe uma comunicação configurada para aquela situação.",
  },
  {
    title: "A mensagem é preparada",
    description: "As informações do cliente e do evento são utilizadas para compor a mensagem.",
  },
  {
    title: "O WhatsApp é enviado",
    description: "O cliente recebe a comunicação no momento certo.",
  },
  {
    title: "Sua equipe continua trabalhando",
    description: "Sem precisar interromper o fluxo para enviar aquela mensagem manualmente.",
  },
];

// Momentos da operação que podem virar comunicação automática pelo WhatsApp.
const segmentos = [
  {
    icon: ShoppingCartIcon,
    titulo: "Vendas",
    texto: "Confirmações e informações relacionadas aos pedidos.",
  },
  {
    icon: TruckIcon,
    titulo: "Delivery",
    texto: "Atualizações sobre preparação, entrega e retirada.",
  },
  {
    icon: WrenchScrewdriverIcon,
    titulo: "Serviços",
    texto: "Confirmações e comunicações relacionadas aos atendimentos.",
  },
  {
    icon: CalendarDaysIcon,
    titulo: "Agenda",
    texto: "Confirmações e lembretes de horários.",
  },
  {
    icon: DocumentCheckIcon,
    titulo: "Documentos",
    texto: "Envio de documentos e arquivos relacionados à operação.",
  },
  {
    icon: ChatBubbleLeftRightIcon,
    titulo: "Relacionamento",
    texto: "Comunicações automatizadas relacionadas aos fluxos configurados pela empresa.",
  },
];

const faqItems = [
  {
    question: "O WhatsApp funciona integrado ao Kali Gestão?",
    answer:
      "Sim. A proposta do recurso é conectar a comunicação pelo WhatsApp aos processos realizados dentro do Kali Gestão.",
  },
  {
    question: "Preciso enviar cada mensagem manualmente?",
    answer:
      "Em fluxos configurados para automação, o sistema realiza o envio de acordo com os eventos definidos.",
  },
  {
    question: "Posso personalizar as mensagens?",
    answer:
      "Sim. Os modelos podem utilizar informações disponíveis no sistema para contextualizar a comunicação.",
  },
  {
    question: "Funciona com agendamentos?",
    answer:
      "Sim. O recurso complementa os processos de agenda e serviços, com comunicações relacionadas aos agendamentos.",
  },
  {
    question: "Como solicito a ativação do módulo?",
    answer:
      "Pelo botão \"Solicitar ativação\" desta página, que leva direto ao Portal do Cliente.",
  },
];
</script>

<template>
  <div class="min-h-screen bg-white">
    <SegmentHero title="O Kali Gestão trabalha. Seu cliente fica sabendo."
      description="Automatize mensagens pelo WhatsApp a partir das ações realizadas no Kali Gestão. Confirmações, atualizações de pedidos, lembretes e documentos chegam ao cliente no momento certo."
      primary-label="Solicitar ativação" :primary-href="portalUrl" :primary-opens-demo="false"
      secondary-label="Ver Kali Gestão" secondary-href="/solucao/kali-gestao" :background-image="heroBg"
      background-class="bg-slate-900 bg-cover bg-center" overlay-variant="left" />

    <!-- Visão geral -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Não é só WhatsApp. É comunicação conectada à sua operação.
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Quando cada mensagem depende de alguém lembrar de abrir o
            WhatsApp, localizar o cliente e escrever o texto, a comunicação
            fica mais lenta e sujeita a esquecimentos. Com o WhatsApp
            integrado, sua equipe continua trabalhando e o Kali Gestão cuida
            da comunicação.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="cap in capacidades" :key="cap.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="cap.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ cap.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ cap.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Como funciona -->
    <section class="py-16 bg-gray-100 scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            Uma ação no sistema. Uma comunicação no momento certo.
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Do evento no Kali Gestão à mensagem no WhatsApp do cliente.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-5">
          <div v-for="(step, idx) in steps" :key="step.title" v-reveal class="p-7 bg-white rounded-3xl shadow-sm">
            <div
              class="inline-flex items-center justify-center w-10 h-10 font-bold text-white rounded-xl bg-kali-blue-600">
              {{ idx + 1 }}
            </div>
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ step.title }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ step.description }}
            </p>
          </div>
        </div>

      </div>
    </section>

    <!-- Diferentes operações, a mesma integração -->
    <section class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]">
            O WhatsApp acompanha diferentes momentos do seu negócio
          </h2>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <div v-for="s in segmentos" :key="s.titulo" v-reveal class="p-6 bg-[#e6f0f4] rounded-2xl">
            <component :is="s.icon" class="w-8 h-8 text-kali-blue-600" />
            <p class="mt-4 text-base font-bold text-gray-900">{{ s.titulo }}</p>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ s.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <FaqPanel title="Dúvidas sobre WhatsApp" :items="faqItems" cta-label="Solicitar ativação" :cta-href="portalUrl"
      description="Respostas rápidas sobre o módulo WhatsApp. Se preferir, fale com nosso time e te orientamos no melhor cenário." />

    <CTA :activation-href="portalUrl" />
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
